// AICA Advanced Select Component - Meridian Dark Design System
// Built with Radix UI for enhanced accessibility and features
// Supports single/multi-select, search, groups, icons, avatars

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp, X, Search, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CONTROL_HEIGHT, CONTROL_SQUARE } from './control-size';
import { useTranslation } from 'react-i18next';

// ====================
// Types & Interfaces
// ====================

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  avatar?: string;
  description?: string;
  group?: string;
}

export interface SelectProps extends Omit<
  SelectPrimitive.SelectProps,
  'value' | 'onValueChange' | 'defaultValue'
> {
  options?: SelectOption[];
  placeholder?: string;
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  clearable?: boolean;
  searchable?: boolean;
  multiple?: boolean;
  value?: string | string[];
  /**
   * Initial selection for uncontrolled use (omit `value`). Read once, on mount.
   *
   * Redeclared rather than inherited: this component reimplements Radix's
   * behaviour with its own state, so the inherited `defaultValue` type promised
   * a prop nothing read — it landed in `...props` and vanished, leaving the
   * trigger showing its placeholder no matter what the caller passed. Typed to
   * mirror `value`, which is what it seeds.
   */
  defaultValue?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  label?: string;
  helperText?: string;
  className?: string;
  emptyMessage?: string;
  maxHeight?: number;
  /** Forwarded to the trigger element so an external `<Label htmlFor>` can
   *  associate with the control (WCAG 1.3.1 programmatic label). */
  id?: string;
  /** Forwarded to the trigger; use when adjacent visible text names the
   *  control (each row gets a unique accessible name). */
  'aria-labelledby'?: string;
  /** Explicit accessible name for controls without a visible label. */
  'aria-label'?: string;
  /** IDs of additional descriptive content outside this component. */
  'aria-describedby'?: string;
  'data-testid'?: string;
}

// ====================
// Select Trigger
// ====================

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: 'sm' | 'md' | 'lg';
    error?: boolean;
    loading?: boolean;
  }
>(({ className, children, size = 'md', error, loading, ...props }, ref) => {
  const sizeStyles = {
    // Heights come from CONTROL_HEIGHT (sm 32, md 36, lg 40) so mixed controls line up.
    sm: cn(CONTROL_HEIGHT.sm, 'px-2 text-xs'),
    md: cn(CONTROL_HEIGHT.md, 'px-3 text-sm'),
    lg: cn(CONTROL_HEIGHT.lg, 'px-4 text-input-lg'),
  };

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        // Base styles
        'flex w-full items-center justify-between rounded-input border',
        'bg-bg-surface text-fg',
        'border-border',

        // Focus states
        'focus:outline-none focus:ring-2 focus:ring-primary/30',
        'focus:border-primary focus:shadow-glow-subtle',

        // Hover state
        'hover:border-border-emphasis',

        // Disabled state
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-bg-subtle',

        // Error state
        error && [
          'border-danger',
          'focus:border-danger focus:ring-danger/30',
          'focus:shadow-glow-error',
        ],

        // Loading state
        loading && 'cursor-wait',

        // Size variant
        sizeStyles[size],

        // Transitions
        'transition-all duration-150 ease-standard',
        'data-[placeholder]:text-fg-muted',

        className
      )}
      {...props}
    >
      <span className="flex items-center gap-2 flex-1 truncate">{children}</span>
      <div className="flex items-center gap-1">
        {loading && <Loader2 className="h-4 w-4 animate-spin text-fg-subtle" />}
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </SelectPrimitive.Icon>
      </div>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// ====================
// Select Content
// ====================

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    maxHeight?: number;
    /** Search input element to render in sticky header */
    searchInput?: React.ReactNode;
  }
>(
  (
    { className, children, position = 'popper', maxHeight = 300, searchInput, ...props },
    ref
  ) => (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          // Base styles
          'relative z-dropdown overflow-hidden rounded-input border',
          'bg-bg-elevated border-border shadow-xl',

          // Position styles
          position === 'popper' && [
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2',
            'data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2',
            'data-[side=top]:slide-in-from-bottom-2',
          ],

          className
        )}
        position={position}
        style={{ maxHeight: `${maxHeight}px`, minWidth: '200px', minHeight: '100px' }}
        {...props}
      >
        {/* Sticky search input at top */}
        {searchInput && (
          <div className="sticky top-0 z-10 bg-bg-elevated border-b border-border px-2 py-2">
            {searchInput}
          </div>
        )}

        <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1 bg-bg-elevated">
          <ChevronUp className="h-4 w-4" />
        </SelectPrimitive.ScrollUpButton>

        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>

        <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1 bg-bg-elevated">
          <ChevronDown className="h-4 w-4" />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

// ====================
// Select Item
// ====================

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    icon?: React.ReactNode;
    avatar?: string;
    description?: string;
  }
>(({ className, children, icon, avatar, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      // Base styles
      'relative flex w-full cursor-pointer select-none items-center',
      'rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none',

      // Focus & hover states
      'focus:bg-bg-subtle focus:text-fg',
      'hover:bg-bg-subtle',

      // Selected state
      'data-[state=checked]:bg-primary-dim',

      // Disabled state
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',

      // Transitions
      'transition-all duration-150',

      className
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-primary" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <div className="flex items-center gap-2 flex-1">
      {avatar && (
        <img src={avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
      )}
      {icon && !avatar && (
        <span className="flex h-5 w-5 items-center justify-center text-fg-subtle">
          {icon}
        </span>
      )}
      <div className="flex flex-col">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {description && (
          <span className="text-xs text-fg-muted mt-0.5">{description}</span>
        )}
      </div>
    </div>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

// ====================
// Select Group
// ====================

const SelectGroup = SelectPrimitive.Group;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-semibold text-fg-subtle', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// ====================
// Main Select Component
// ====================

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      options = [],
      placeholder,
      error,
      size = 'md',
      loading,
      clearable,
      searchable,
      multiple,
      value,
      defaultValue,
      onValueChange,
      label,
      helperText,
      className,
      emptyMessage,
      maxHeight = 300,
      disabled,
      id,
      'aria-labelledby': ariaLabelledBy,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const resolvedPlaceholder = placeholder ?? t('common:common.selectOption');
    const resolvedEmptyMessage = emptyMessage ?? t('common:common.noOptionsFound');
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    // Controlled when the caller passes `value`; uncontrolled (seeded once from
    // `defaultValue`) otherwise. Kept in a ref so a caller cannot flip modes
    // mid-life and silently change which side owns the selection.
    const isControlled = React.useRef(value !== undefined).current;
    const [internalValue, setInternalValue] = React.useState<string | string[]>(() => {
      const initial = isControlled ? value : defaultValue;
      return multiple ? (initial as string[]) || [] : (initial as string) || '';
    });
    const helperTextId = `${React.useId()}-${error ? 'error' : 'helper'}`;
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Sync internal value with the controlled value prop.
    //
    // Uncontrolled callers must be skipped, not merely seeded above: `value` is
    // undefined for them, so this effect would run on mount and reset the seed
    // to '' — the trigger would fall back to its placeholder and the caller's
    // defaultValue would never be seen.
    React.useEffect(() => {
      if (!isControlled) return;
      if (multiple) {
        setInternalValue((value as string[]) || []);
      } else {
        setInternalValue((value as string) || '');
      }
    }, [value, multiple, isControlled]);

    // Filter options based on search
    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchQuery) return options;

      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          option.value.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [options, searchQuery, searchable]);

    // Group options
    const groupedOptions = React.useMemo(() => {
      const groups: { [key: string]: SelectOption[] } = {};
      const ungrouped: SelectOption[] = [];

      filteredOptions.forEach((option) => {
        if (option.group) {
          if (!groups[option.group]) {
            groups[option.group] = [];
          }
          groups[option.group]?.push(option);
        } else {
          ungrouped.push(option);
        }
      });

      return { groups, ungrouped };
    }, [filteredOptions]);

    // Handle value change
    const handleValueChange = React.useCallback(
      (newValue: string) => {
        if (multiple) {
          const currentValues = (internalValue as string[]) || [];
          const updatedValues = currentValues.includes(newValue)
            ? currentValues.filter((v) => v !== newValue)
            : [...currentValues, newValue];

          setInternalValue(updatedValues);
          onValueChange?.(updatedValues);
        } else {
          setInternalValue(newValue);
          onValueChange?.(newValue);
          setOpen(false);
        }
      },
      [multiple, internalValue, onValueChange]
    );

    // Handle clear
    const handleClear = React.useCallback(() => {
      const emptyValue = multiple ? [] : '';
      setInternalValue(emptyValue);
      onValueChange?.(emptyValue);
    }, [multiple, onValueChange]);

    // Get display value
    const getDisplayValue = React.useCallback(() => {
      if (multiple) {
        const values = internalValue as string[];
        if (values.length === 0) return resolvedPlaceholder;
        const labels = values.map(
          (v) => options.find((opt) => opt.value === v)?.label || v
        );
        return labels.join(', ');
      } else {
        if (!internalValue) return resolvedPlaceholder;
        if (options.length === 0) {
          // Options can still be loading (e.g. an id-bound Select whose
          // value is seeded synchronously from props/initialData while its
          // option list comes from a query that hasn't resolved yet). With
          // nothing to compare against, showing the raw id would flash a
          // UUID at the user; the placeholder correctly reads as "loading".
          return resolvedPlaceholder;
        }
        const option = options.find((opt) => opt.value === internalValue);
        if (!option) {
          // A stored value can outlive the option list that produced it (a
          // trimmed or since-renamed list). Falling back to the placeholder
          // here would make saved data look lost, so show the raw value
          // instead of silently discarding it.
          return internalValue as string;
        }
        return (
          <span className="flex items-center gap-2">
            {option.avatar && (
              <img src={option.avatar} alt="" className="h-5 w-5 rounded-full" />
            )}
            {option.icon && !option.avatar && option.icon}
            {option.label}
          </span>
        );
      }
    }, [internalValue, multiple, options, resolvedPlaceholder]);

    const hasValue = multiple ? (internalValue as string[]).length > 0 : !!internalValue;
    const describedBy = [ariaDescribedBy, helperText ? helperTextId : undefined]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={cn('w-full', className)} data-testid={dataTestId}>
        {label && (
          <label className="mb-1.5 block text-sm font-medium text-fg">{label}</label>
        )}

        <div className="flex items-stretch gap-1">
          <SelectPrimitive.Root
            value={multiple ? undefined : (internalValue as string)}
            onValueChange={multiple ? undefined : handleValueChange}
            open={open}
            onOpenChange={setOpen}
            disabled={disabled || loading}
            {...props}
          >
            <SelectTrigger
              ref={ref}
              id={id}
              size={size}
              error={error}
              loading={loading}
              className="min-w-0 flex-1"
              // When an external label (`id` -> <Label htmlFor>) or
              // `aria-labelledby` names the control, suppress the generic
              // fallback unless the caller supplied an explicit aria-label.
              aria-label={
                ariaLabel ??
                (id || ariaLabelledBy ? undefined : label || resolvedPlaceholder)
              }
              aria-labelledby={ariaLabelledBy}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={describedBy || undefined}
            >
              <SelectPrimitive.Value placeholder={resolvedPlaceholder}>
                {getDisplayValue()}
              </SelectPrimitive.Value>
            </SelectTrigger>

            <SelectContent
              maxHeight={maxHeight}
              searchInput={
                searchable ? (
                  <div className="flex items-center gap-2.5 px-3 py-2 bg-bg-surface rounded-input border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
                    <Search className="h-4 w-4 text-fg-muted shrink-0" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchQuery(newValue);
                        // Restore focus after React re-render completes
                        // Use double RAF to ensure DOM is fully updated
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            searchInputRef.current?.focus();
                          });
                        });
                      }}
                      placeholder={t('common:common.searchPlaceholder')}
                      className="flex-1 bg-transparent text-sm outline-none placeholder:text-fg-muted min-w-0"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onBlur={(e) => {
                        // Prevent blur when clicking within the select content
                        e.preventDefault();
                        // Immediately restore focus if still open
                        setTimeout(() => {
                          if (open && searchInputRef.current) {
                            searchInputRef.current.focus();
                          }
                        }, 0);
                      }}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery('');
                          // Restore focus to search input after clearing
                          requestAnimationFrame(() => {
                            searchInputRef.current?.focus();
                          });
                        }}
                        className="p-0.5 rounded hover:bg-bg-subtle text-fg-muted hover:text-fg"
                        aria-label={t('common:common.clearSearch')}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ) : undefined
              }
            >
              {filteredOptions.length === 0 && (
                <div className="py-6 text-center text-sm text-fg-muted">
                  {resolvedEmptyMessage}
                </div>
              )}

              {/* Ungrouped options */}
              {groupedOptions.ungrouped.map((option) => (
                <SelectItem
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  icon={option.icon}
                  avatar={option.avatar}
                  description={option.description}
                  onSelect={() => multiple && handleValueChange(option.value)}
                >
                  {option.label}
                </SelectItem>
              ))}

              {/* Grouped options */}
              {Object.entries(groupedOptions.groups).map(([groupName, groupOptions]) => (
                <React.Fragment key={groupName}>
                  {groupedOptions.ungrouped.length > 0 && <SelectSeparator />}
                  <SelectGroup>
                    <SelectLabel>{groupName}</SelectLabel>
                    {groupOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        icon={option.icon}
                        avatar={option.avatar}
                        description={option.description}
                        onSelect={() => multiple && handleValueChange(option.value)}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </React.Fragment>
              ))}
            </SelectContent>
          </SelectPrimitive.Root>

          {clearable && hasValue && !loading && (
            <button
              type="button"
              onClick={handleClear}
              disabled={disabled}
              className={cn(
                CONTROL_SQUARE[size],
                'inline-flex shrink-0 items-center justify-center rounded-input border border-border bg-bg-surface text-fg-muted transition-all duration-150 hover:border-border-emphasis hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50'
              )}
              aria-label={t('common:common.clearSelection')}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {helperText && (
          <p
            id={helperTextId}
            role={error ? 'alert' : undefined}
            className={cn('mt-1.5 text-xs', error ? 'text-danger' : 'text-fg-muted')}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);
Select.displayName = 'Select';

// Re-export SelectValue from Radix for convenience
const SelectValue = SelectPrimitive.Value;
SelectValue.displayName = SelectPrimitive.Value.displayName;

// Export Root for composition pattern (when using SelectTrigger + children directly)
// Use SelectRoot when you want to render your own SelectItem children
// Use Select when you want to pass options prop and let component render items
const SelectRoot = SelectPrimitive.Root;
SelectRoot.displayName = 'SelectRoot';

export {
  Select,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
};
